import {Component, HostListener, OnInit} from '@angular/core';
import {SortOrder} from '../../model/sort-order';
import {TriggersService} from '../../services/triggers.service';
import {MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {Trigger} from '../../model/trigger';
import {CommonMessagesService} from '../../services/common-messages.service';
import {TriggerFilters} from '../../model/filters/trigger-filters';
import {FiltersService} from '../../services/filters.service';

@Component({
  selector: 'app-events',
  templateUrl: './triggers.component.html',
  styleUrls: ['./triggers.component.scss']
})
export class TriggersComponent implements OnInit {

  public triggers: Trigger[];
  public totalTriggers: number;
  public loadingTriggers: boolean;

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public currentFilters: TriggerFilters;

  public activeButtonState: boolean;
  public selected: Trigger[];
  public filtersFilled: boolean;

  constructor(private messageService: MessageService,
              private i18n: TranslocoService,
              private triggersService: TriggersService,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<TriggerFilters>) {
    this.selected = [];
    this.activeButtonState = true;
    this.currentFilters = new TriggerFilters();
  }

  public ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    this.triggers = new Array(this.rows);
  }

  private loadTriggers(offset: number, limit: number): void {
    this.loadingTriggers = true;
    this.triggers = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.triggersService.getTriggers(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters : this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        response.triggers.forEach((trigger: Trigger) => {
          trigger['dataKey'] = `${trigger.name}-${trigger.entityType === 'DEVICE' ? trigger.device.uuid : trigger.asset.uuid}-${trigger.ownerUuid}`;
        });
        this.triggers = response.triggers;
        this.totalTriggers = response.totalElements;
        this.selected = [];
        this.loadingTriggers = false;
      });
  }

  public onPageChange(event: any): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadTriggers(event.first / event.rows, event.rows);
  }

  public resetFilters(): void {
    this.currentFilters = new TriggerFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  public applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadTriggers(this.first / this.rows, this.rows);
  }

  public setSelectedEntityType(type: string): void {
    this.currentFilters.serialNumber = null;
    this.currentFilters.alias = null;
    this.currentFilters.entityType = type;
  }

  public setSelectedRuleType(isUserTrigger: boolean): void {
    this.currentFilters.isUserTrigger = isUserTrigger;
  }

  public evaluateActiveButtonState(): void {
    let state: boolean;
    this.selected.forEach(event => {
      if (event.active === true) {
        state = true;
      } else if (event.active === false) {
        if (state === undefined) {
          state = false;
        }
      }
    });
    this.activeButtonState = !state;
  }

  public changeActiveStates(): void {
    this.triggersService.updateTriggerState(this.selected, !this.activeButtonState).toPromise().then(() => {
      const summary = this.i18n.translate('success.generic.changesApplied');
      const detail = this.i18n.translate('success.triggers.changedState');
      this.messageService.add({severity: 'success', summary, detail});
      this.loadTriggers(this.first / this.rows, this.rows);
    });
  }

  public confirmAndDelete(trigger: Trigger): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      if (trigger.entityType === 'DEVICE') {
        this.deleteTrigger('DEVICE', trigger.device.uuid, trigger.deviceMeasurement.uuid, trigger.name);
      } else if (trigger.entityType === 'ASSET') {
        this.deleteTrigger('ASSET', trigger.asset.uuid, trigger.assetMeasurement.uuid, trigger.name);
      }
    });
  }
  private deleteTrigger(entityType: string, entityUuid: string, entityMeasurementUuid: string, triggerName: string): void {
    this.triggersService.deleteTrigger(entityType, entityUuid, entityMeasurementUuid, triggerName).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadTriggers(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }
  public confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  private deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const identifiers = this.selected.map(item => {
      if (item.entityType === 'DEVICE') {
        return ({
          entityType: 'DEVICE',
          entityUuid: item.device.uuid,
          entityMeasurementUuid: item.deviceMeasurement.uuid,
          triggerName: item.name
        });
      } else if (item.entityType === 'ASSET') {
        return ({
          entityType: 'ASSET',
          entityUuid: item.asset.uuid,
          entityMeasurementUuid: item.assetMeasurement.uuid,
          triggerName: item.name
        });
      }
    });

    this.triggersService.deleteTriggers(identifiers).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadTriggers(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  private trimFieldsForm(filters: TriggerFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
